import { UserPasswordValidationTip } from '@flippingbook/web.react-kit/dist/UserPasswordValidationTip';
import { ErrorMessage } from '@hookform/error-message';
import cx from 'classnames';
import { useCallback, useState } from 'react';
import type { FieldValues, MultipleFieldErrors, UseFormReturn } from 'react-hook-form';

import { useStartTyping } from '@/hooks/useStartTyping';
import { loc } from '@/localization';
import { FieldErrorBox } from '@/site/components/FieldErrorBox';
import { UserPasswordInput } from '@/site/components/UserPasswordInput';
import { getPolicyErrorMessage, userPasswordPoliciesList } from '@/utils/userPasswordPolicies';

import type { Field } from '../Field';

import { b, e } from './style.css';

export function UserPasswordField<T extends FieldValues>(props: UserPasswordField.Props<T>) {
    const {
        autoFocus,
        defaultValue,
        disabled,
        readOnly,
        watch,
        errors,
        name,
        onBlur,
        onChange,
        placeholder,
        register,
        isSubmitted,
        triggerValidation,
        size = 'large',
        validationRules = {},
        className: propsClassName,
        onStartTyping,
    } = props;

    const { handleChange: handleTypingChange, handleBlur: handleTypingBlur } = useStartTyping({ onStartTyping });

    const [focused, setFocused] = useState(false);

    const passwordPoliciesErrors = Object.values(errors[name]?.types || {});

    const handleChange = useCallback(
        (ev: React.ChangeEvent<HTMLInputElement>) => {
            void triggerValidation?.(name);
            handleTypingChange(ev);
            onChange?.(ev);
        },
        [handleTypingChange, triggerValidation, name, onChange],
    );

    const handleFocus = useCallback(() => {
        setFocused(true);
    }, []);

    const handleBlur = useCallback(
        (ev: React.FocusEvent<HTMLInputElement>) => {
            setFocused(false);
            handleTypingBlur();
            onBlur?.(ev);
        },
        [handleTypingBlur, onBlur],
    );

    const renderErrors = useCallback(
        ({ message, messages }: { message: string; messages?: MultipleFieldErrors }) => {
            let errors = message;
            if (messages) {
                errors = getPolicyErrorMessage(passwordPoliciesErrors as string[]);
            }
            return <FieldErrorBox className={e.error}>{errors}</FieldErrorBox>;
        },
        [passwordPoliciesErrors],
    );

    const password = watch(name);
    const passwordIsEmpty = !password;
    const validationTipHidden = !focused || passwordIsEmpty;
    const passwordErrorsVisible = isSubmitted && !!errors[name] && validationTipHidden;

    return (
        <div className={cx(b.name, propsClassName)}>
            <UserPasswordInput
                {...register(name, {
                    disabled,
                    onBlur: handleBlur,
                    onChange: handleChange,
                    ...validationRules[name],
                })}
                validationTip={
                    <UserPasswordValidationTip
                        title={`${loc.Validation.PasswordMustContain}:`}
                        policies={userPasswordPoliciesList}
                        errors={passwordPoliciesErrors}
                    />
                }
                inputSize={size}
                defaultValue={defaultValue}
                disabled={disabled}
                readOnly={readOnly}
                placeholder={placeholder}
                state={passwordErrorsVisible ? 'error' : 'default'}
                autoFocus={autoFocus}
                title={placeholder}
                onFocus={handleFocus}
            />
            {passwordErrorsVisible && (
                <ErrorMessage
                    errors={errors}
                    name={name}
                    render={renderErrors}
                />
            )}
        </div>
    );
}

export namespace UserPasswordField {
    export interface Props<T extends FieldValues> extends Field.Props<T> {
        isSubmitted: boolean;
        watch: UseFormReturn<T>['watch'];
    }
}
